import {
  CreateSpecializationServiceDto,
  CreateSpecializationServiceDtoCategoryEnum,
  CreateSpecializationServiceDtoVisitTypeEnum,
  GetProfessionalConfigDtoEnabledCategoriesEnum,
  UpdateSpecializationServiceDto,
} from 'woofwoof-api';

export interface OrganizationServiceFormValues {
  specializationId: string;
  name: string;
  duration: number | '';
  price: number | '';
  prepaidAmount: number | '';
  description: string;
}

export interface ServicePricingFields {
  showsPrice: boolean;
  showsPrepayment: boolean;
}

// The visit type decides what is paid online: onsite and mobile visits take
// only the prepayment and the price is paid at the facility, online visits
// take the full price. A veterinarian's facility price is not published.
export const servicePricingFields = (
  visitType: string,
  category: string,
): ServicePricingFields => {
  const isFacilityVisit = visitType === 'onsite' || visitType === 'mobile';
  return {
    showsPrice:
      !isFacilityVisit ||
      category !== GetProfessionalConfigDtoEnabledCategoriesEnum.Veterinarian,
    showsPrepayment: isFacilityVisit,
  };
};

// The form resolver casts a blank amount to undefined.
const isBlank = (value: number | '' | undefined): boolean =>
  value === '' || value === undefined;

const amount = (value: number | '' | undefined): number =>
  isBlank(value) ? 0 : Number(value);

export const toCreateServiceDto = (
  values: OrganizationServiceFormValues,
  category: string,
  visitType: string,
): CreateSpecializationServiceDto => {
  const { showsPrice, showsPrepayment } = servicePricingFields(
    visitType,
    category,
  );
  return {
    name: values.name,
    description: values.description || '',
    specializationId: values.specializationId,
    category: category as CreateSpecializationServiceDtoCategoryEnum,
    visitType: visitType as CreateSpecializationServiceDtoVisitTypeEnum,
    defaultPrice: showsPrice ? amount(values.price) : 0,
    defaultDuration: Number(values.duration),
    ...(showsPrepayment &&
      !isBlank(values.prepaidAmount) && {
        prepaidAmount: Number(values.prepaidAmount),
      }),
  };
};

export const toUpdateServiceDto = (
  values: OrganizationServiceFormValues,
  category: string,
  visitType: string,
): UpdateSpecializationServiceDto => {
  const { showsPrice, showsPrepayment } = servicePricingFields(
    visitType,
    category,
  );
  return {
    name: values.name,
    description: values.description || '',
    specializationId: values.specializationId,
    defaultDuration: Number(values.duration),
    // A price the form does not show stays as stored.
    ...(showsPrice && { defaultPrice: amount(values.price) }),
    // An omitted prepayment keeps the stored one; 0 clears it. Online
    // services never take one, so a stale stored fee is cleared on save.
    prepaidAmount: showsPrepayment ? amount(values.prepaidAmount) : 0,
  };
};
